//dependencies
import React, { useMemo, useState } from "react";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";

import { Pagination, SortChangedEvent, Table } from "@performyard/py-components";

//components
import Button from "../../../../../components/Button";
import ReviewsOfMeFiltersPanel, { ReviewsOfMeFilters } from "./components/ReviewsOfMeFilterPanel";
import ReviewsOfMeReadableFilters from "./components/ReviewsOfMeReadableFilters";
import ReviewsOfMeTableCols from "./components/ReviewsOfMeTableCols";

//hooks
import { useSearchService, useSearchServicePrefetch } from "../../../../../api/hooks";
import useSearchQueryParams from "../../../../../hooks/useSearchQueryParams";
import useUserReviewsNavLinksAndPermissions from "../../hooks/useUserReviewsNavLinksAndPermissions";

//services
import { SERVICE_REVIEW } from "../../../../../api/services/defs/search-service";
import { Review } from "../../../../../api/services/searchServiceResultTypes/reviews";

//utils
import { getFiltersForSearchService } from "./utils";

//page template
import InfoPageTemplate from "../../../../../pageTemplates/InfoPageTemplate";
import StandardToolbarActionRow from "../../../../../pageTemplates/InfoPageTemplate/StandardToolbarActionRow";
import useInfoPageTemplateQueryState from "../../../../../pageTemplates/InfoPageTemplate/useInfoPageTemplateQueryState";
//constants
import { REVIEWS_TABLE_LIMIT, USER_REVIEWS_CONFIG } from "../../constants";

const ReviewsOfMe = () => {
	const { activePage, navLinks, employeeId, isEasyAddEnabled, userCanCreateReviews, userCanAdd360Forms } =
		useUserReviewsNavLinksAndPermissions();
	const { handleQueryChange } = useSearchQueryParams({ schema: USER_REVIEWS_CONFIG.queryParametersSchema });

	const {
		pageNumber,
		setPageNumber,
		sort,
		setSort,
		filters,
		setFilters,
		searchValue,
		debouncedSearchValue,
		setDebouncedSearch,
	} = useInfoPageTemplateQueryState<ReviewsOfMeFilters>({
		defaultSortName: "start_date",
		defaultSortDirection: "desc",
		defaultPageNumber: 1,
	});

	const searchFilters = useMemo(
		() => getFiltersForSearchService(filters, employeeId, debouncedSearchValue),
		[JSON.stringify(filters), employeeId, debouncedSearchValue]
	);

	const { data, isLoading } = useSearchService<Review>({
		service: SERVICE_REVIEW,
		filters: searchFilters,
		skip: (pageNumber - 1) * REVIEWS_TABLE_LIMIT,
		limit: REVIEWS_TABLE_LIMIT,
		sort,
		defaultErrorMessage: "There was an error getting Reviews of Me. Please try again.",
	});

	//prefetch 3 pages ahead and 1 back
	useSearchServicePrefetch<Review>(
		{
			service: SERVICE_REVIEW,
			filters: searchFilters,
			skip: (pageNumber - 2) * REVIEWS_TABLE_LIMIT,
			limit: REVIEWS_TABLE_LIMIT,
			sort,
			disabled: isLoading,
		},
		pageNumber > 1 ? 1 : 0
	);

	useSearchServicePrefetch<Review>(
		{
			service: SERVICE_REVIEW,
			filters: searchFilters,
			skip: pageNumber * REVIEWS_TABLE_LIMIT,
			limit: REVIEWS_TABLE_LIMIT,
			sort,
			disabled: isLoading,
		},
		3
	);

	const forms = data?.results || [];
	const totalForms = data?.total || 0;

	const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false);

	const additionalFiltersCount = Object.keys(filters).length;

	const columnDefs = useMemo(() => ReviewsOfMeTableCols(isEasyAddEnabled), [isEasyAddEnabled]);

	const onSortChanged = (event: SortChangedEvent<Review>) => {
		const columnState = event.api.getColumnState();
		const sortedColumn = columnState.find((col) => col.sort);
		if (sortedColumn?.colId) {
			setSort({
				name: sortedColumn.colId,
				direction: sortedColumn.sort as "asc" | "desc",
			});
		}
	};

	const toolbarActionRow = (
		<StandardToolbarActionRow
			searchInput={searchValue}
			onSearchInputChange={(value) => setDebouncedSearch(value)}
			onFilterButtonPress={() => {
				setIsFiltersPanelOpen(true);
			}}
			activeFiltersCount={additionalFiltersCount}
			searchPlaceholder="Search reviews..."
		/>
	);

	const drawer = (
		<ReviewsOfMeFiltersPanel
			isOpen={isFiltersPanelOpen}
			onClose={() => setIsFiltersPanelOpen(false)}
			filters={filters}
			onApplyFilters={(newFilters) => {
				setFilters(newFilters);
			}}
		/>
	);

	const toolbarInformationRow = <ReviewsOfMeReadableFilters filters={filters} setFilters={setFilters} />;

	const mainContent = (
		<div>
			<Table<Review>
				autoHeight
				rowData={forms}
				columnDefs={columnDefs}
				loading={isLoading}
				getRowId={(params) => params.data._id}
				onSortChanged={onSortChanged}
				emptyStateText="No Reviews Found"
				initialState={{
					sort: {
						sortModel: sort.name ? [{ colId: sort.name, sort: sort.direction as "asc" | "desc" }] : [],
					},
				}}
				animateRows={false}
			/>
			{totalForms > 0 && (
				<div className="mx-auto mt-5 flex w-full max-w-[400px] justify-center">
					<Pagination
						currentPage={pageNumber}
						pageCount={Math.ceil(totalForms / REVIEWS_TABLE_LIMIT)}
						onPageChange={(newPage: number) => setPageNumber(newPage)}
					/>
				</div>
			)}
		</div>
	);

	const actionButtons = [
		userCanAdd360Forms && (
			<Button data-testid="create360FormButton" onClick={() => handleQueryChange({ modal: "add360Form" })}>
				Add 360 Form
			</Button>
		),
		userCanCreateReviews && (
			<Button
				data-testid="createReviewButton"
				onClick={() => handleQueryChange({ modal: "createReview" })}
				color="green"
				icon={faPlus}
			>
				Create Review
			</Button>
		),
	];

	return (
		<InfoPageTemplate
			employeeID={employeeId}
			toolbarActionRow={toolbarActionRow}
			toolbarInformationRow={toolbarInformationRow}
			actionButtons={actionButtons}
			mainContent={mainContent}
			drawer={drawer}
			navigationLinks={navLinks}
			activeNavigationLinkKey={activePage}
		/>
	);
};

export default ReviewsOfMe;
